import { useState, useMemo, useCallback } from 'react';
import {
  Alert, Avatar, Box, Button, Card, CardContent, CardHeader,
  Dialog, DialogActions, DialogContent, DialogTitle, Grid, InputAdornment,
  LinearProgress, List, ListItem, ListItemAvatar, ListItemText, MenuItem,
  Stack, Switch, TextField as MuiTextField, Typography,
} from '@mui/material';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import SearchIcon from '@mui/icons-material/Search';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import TuneIcon from '@mui/icons-material/Tune';
import configurationApi from '../../services/configurationApi';
import { FEATURE_METADATA } from '../../services/featureFlagService';
import StatusBadge from '../../components/shared/StatusBadge';
import PageHeader from '../../components/shared/PageHeader';
import LoadingSkeleton from '../../components/shared/LoadingSkeleton';

const FeatureManagementPage = () => {
  const { data: backendFlags = {}, isLoading } = configurationApi.endpoints.getFeatureFlags.useQuery();
  const [saveFlags] = configurationApi.endpoints.saveFeatureFlags.useMutation();

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');

  // Merge backend flags with FEATURE_METADATA, grouped by category
  const featuresByCategory = useMemo(() => {
    const grouped = {};
    Object.keys(FEATURE_METADATA).forEach(key => {
      const metadata = FEATURE_METADATA[key];
      const category = metadata.category;
      if (!grouped[category]) grouped[category] = [];

      grouped[category].push({
        key,
        ...metadata,
        enabled: backendFlags[key] ?? false,
      });
    });
    return grouped;
  }, [backendFlags]);

  const showMessage = useCallback((msg) => {
    setSaveMessage(msg);
    setTimeout(() => setSaveMessage(''), 3000);
  }, []);

  // Build the full backend payload from current state, with one key toggled
  const buildPayload = useCallback((overrides = {}) => {
    const payload = {};
    Object.keys(FEATURE_METADATA).forEach(key => {
      if (key in overrides) {
        payload[key] = overrides[key];
      } else {
        payload[key] = backendFlags[key] ?? false;
      }
    });
    return payload;
  }, [backendFlags]);

  const handleToggle = async (featureKey) => {
    const current = backendFlags[featureKey] ?? false;
    await saveFlags(buildPayload({ [featureKey]: !current }));
    showMessage('Feature updated successfully');
  };

  const handleEnableAll = async (category) => {
    const overrides = {};
    (featuresByCategory[category] || []).forEach(f => {
      if (!f.alwaysEnabled) overrides[f.key] = true;
    });
    await saveFlags(buildPayload(overrides));
    showMessage(`All ${category} features enabled`);
  };

  const handleDisableAll = async (category) => {
    const overrides = {};
    (featuresByCategory[category] || []).forEach(f => {
      if (!f.alwaysEnabled) overrides[f.key] = false;
    });
    await saveFlags(buildPayload(overrides));
    showMessage(`All ${category} features disabled`);
  };

  const handleReset = async () => {
    const payload = {};
    Object.keys(FEATURE_METADATA).forEach(key => {
      payload[key] = false;
    });
    await saveFlags(payload);
    setShowResetConfirm(false);
    showMessage('All features reset to default state');
  };

  const getFilteredFeatures = () => {
    let allFeatures = [];
    Object.keys(featuresByCategory).forEach(category => {
      if (selectedCategory === 'all' || selectedCategory === category) {
        allFeatures = [...allFeatures, ...featuresByCategory[category]];
      }
    });
    if (searchTerm) {
      allFeatures = allFeatures.filter(feature =>
        feature.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        feature.description.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    return allFeatures;
  };

  const categories = Object.keys(featuresByCategory);
  const filteredFeatures = getFilteredFeatures();

  const getCategoryStats = (category) => {
    const features = featuresByCategory[category] || [];
    const enabled = features.filter(f => f.enabled).length;
    const total = features.length;
    return { enabled, total };
  };

  const FeatureListItem = ({ feature, divider }) => (
    <ListItem divider={divider} secondaryAction={
      <Stack direction="row" spacing={1} alignItems="center">
        <Typography variant="caption" color={feature.enabled ? 'success.main' : 'text.secondary'}>
          {feature.enabled ? 'Enabled' : 'Disabled'}
        </Typography>
        <Switch
          checked={feature.enabled}
          onChange={() => handleToggle(feature.key)}
          disabled={feature.alwaysEnabled}
          size="small"
        />
      </Stack>
    }>
      <ListItemAvatar>
        <Avatar sx={{ width: 36, height: 36, bgcolor: feature.enabled ? 'primary.main' : 'action.disabledBackground' }}>
          <TuneIcon sx={{ fontSize: 18 }} />
        </Avatar>
      </ListItemAvatar>
      <ListItemText
        primary={
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body2" fontWeight={700}>{feature.name}</Typography>
            {feature.alwaysEnabled && <StatusBadge variant="info">Always Enabled</StatusBadge>}
          </Stack>
        }
        secondary={feature.description}
      />
    </ListItem>
  );

  const CategoryCard = ({ category, features }) => (
    <Card sx={{ mb: 2 }}>
      <CardHeader
        title={<Typography variant="subtitle1" fontWeight={700}>{category}</Typography>}
        action={
          <Stack direction="row" spacing={1}>
            <Button size="small" variant="contained" onClick={() => handleEnableAll(category)}>Enable All</Button>
            <Button size="small" variant="outlined" onClick={() => handleDisableAll(category)}>Disable All</Button>
          </Stack>
        }
      />
      <List disablePadding>
        {features.map((feature, i) => (
          <FeatureListItem key={feature.key} feature={feature} divider={i < features.length - 1} />
        ))}
      </List>
    </Card>
  );

  if (isLoading) {
    return <LoadingSkeleton variant="page" />;
  }

  return (
    <Box>
      <PageHeader
        title="Broker Feature Management"
        subtitle="Control which features and tabs are visible to Broker users"
        actions={
          <Button variant="outlined" color="error" startIcon={<RestartAltIcon />} onClick={() => setShowResetConfirm(true)}>
            Reset All to Default
          </Button>
        }
      />

      {saveMessage && (
        <Alert severity="success" sx={{ mb: 3 }}>{saveMessage}</Alert>
      )}

      {/* Category Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {categories.map(category => {
          const stats = getCategoryStats(category);
          const pct = stats.total > 0 ? (stats.enabled / stats.total) * 100 : 0;
          return (
            <Grid key={category} size={{ xs: 6, md: 3 }}>
              <Card>
                <CardContent>
                  <Typography variant="overline" color="text.secondary">{category}</Typography>
                  <Typography variant="h4" fontWeight={700} sx={{ mt: 1 }}>{stats.enabled}/{stats.total}</Typography>
                  <Typography variant="caption" color="text.secondary">Features Enabled</Typography>
                  <LinearProgress variant="determinate" value={pct} sx={{ mt: 1, height: 4, borderRadius: 2 }} />
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 6 }}>
              <MuiTextField
                size="small" fullWidth placeholder="Search features..."
                value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <MuiTextField
                select size="small" fullWidth
                value={selectedCategory} onChange={(e) => setSelectedCategory(e.target.value)}
              >
                <MenuItem value="all">All Categories</MenuItem>
                {categories.map(category => <MenuItem key={category} value={category}>{category}</MenuItem>)}
              </MuiTextField>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Features by Category */}
      {selectedCategory === 'all'
        ? categories.map(category => (
            <CategoryCard key={category} category={category} features={featuresByCategory[category] || []} />
          ))
        : <CategoryCard category={selectedCategory} features={filteredFeatures} />
      }

      {/* Reset Confirmation Dialog */}
      <Dialog open={showResetConfirm} onClose={() => setShowResetConfirm(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Reset All Features?</DialogTitle>
        <DialogContent>
          <Box sx={{ textAlign: 'center', py: 2 }}>
            <WarningAmberIcon sx={{ fontSize: 48, color: 'error.main', mb: 1 }} />
            <Typography variant="body2" color="text.secondary">
              This will reset all feature flags to their default state. Most features will be disabled.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowResetConfirm(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleReset}>Reset All</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FeatureManagementPage;
